// indexedDB.js

import { api } from '../api/index.js';
import { v4 as uuidv4 } from 'uuid';
// import external encpoint functions
import { sendExternalFeedback } from '../db/externalEndpoints/externalFeedbackService.js';


const DB_NAME = 'dsrh_database'; // Database name indexdb
const DB_VERSION = 1;

let db;

export function initDb() {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      console.error('Error opening database:', event.target.error);
      reject(event.target.error);
    };

    request.onsuccess = (event) => {
      db = event.target.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      db = event.target.result;
      
      // Create object stores if they do not exist with synced field indexed
      if (!db.objectStoreNames.contains('question_1_scale')) {
        const question1Store = db.createObjectStore('question_1_scale', { keyPath: 'id', autoIncrement: true });
        question1Store.createIndex('syncedIndex', 'synced', { unique: false });
        //console.log('question_1_scale object store created');
      }
      if (!db.objectStoreNames.contains('question_2_yesno')) {
        const question2Store = db.createObjectStore('question_2_yesno', { keyPath: 'id', autoIncrement: true });
        question2Store.createIndex('syncedIndex', 'synced', { unique: false });
        //console.log('question_2_yesno object store created');
      }
      if (!db.objectStoreNames.contains('question_3_yesno')) {
        const question3Store = db.createObjectStore('question_3_yesno', { keyPath: 'id', autoIncrement: true });
        question3Store.createIndex('syncedIndex', 'synced', { unique: false });
        //console.log('question_3_yesno object store created');
      }
      if (!db.objectStoreNames.contains('complaint')) {
        const complaintStore = db.createObjectStore('complaint', { keyPath: 'id', autoIncrement: true });
        complaintStore.createIndex('syncedIndex', 'synced', { unique: false });
        //console.log('complaint object store created');
      }
      if (!db.objectStoreNames.contains('settings')) {
        db.createObjectStore('settings', { keyPath: 'name' });
      }
    };
  });
}

// function to read configData as object with fileds - business_token, site_name, latitude and longitude from config page
// and store config data into indexedDB database
export async function insertConfigData(business_token, store_name, latitude, longitude) {
  let result = {
    localDBResponse: false,
    cloudDBResponse: false,
    localDBError: null,
    cloudDBError: null,
  };
  let storeId = '';

  try {
    const db = await initDb(); // Ensure the database is initialized properly

    // Open a transaction for the 'settings' store
    const transaction = db.transaction(['settings'], 'readwrite');
    const objectStore = transaction.objectStore('settings');
    const keyToCheck = 'configData'; // Key to check if config data is already present

    // Check if the key already exists
    const existingRecord = await new Promise((resolve, reject) => {
      const request = objectStore.get(keyToCheck);
      request.onsuccess = () => resolve(request.result);
      request.onerror = (event) => reject(event.target.error);
    });

    if (existingRecord) {
      // Key exists: Update the record in local DB
      storeId = existingRecord.store_id;
      //console.log('Store ID found in the local DB:', storeId);

      const updatedData = {
        name: keyToCheck,
        store_id: storeId,
        business_id: business_token,
        store_name: store_name,
        latitude: latitude,
        longitude: longitude,
      };

      await new Promise((resolve, reject) => {
        const request = objectStore.put(updatedData);
        request.onsuccess = resolve;
        request.onerror = (event) => reject(event.target.error);
      });

      //console.log(`Config data updated in IndexedDB for store ID: ${storeId}`);
      result.localDBResponse = true;

      // Update the record in cloud DB
      const cloudUpdateData = {
        business_id: business_token,
        name: store_name,
        latitude: latitude,
        longitude: longitude,
      };

      try {
        const response = await fetch(`${api}/store/${storeId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(cloudUpdateData),
        });

        if (response.ok) {
          //console.log('Store config data updated in cloud successfully');
          result.cloudDBResponse = true;
        } else {
          console.error('Error updating store config data in cloud:', response.statusText);
          result.cloudDBError = response.statusText;
        }
      } catch (error) {
        console.error('Error updating store config data in cloud:', error);
        result.cloudDBError = error;
      }
    } else {
      // Key does not exist: Add the record in local DB
      storeId = uuidv4(); // Generate a new UUID for the store

      const newData = {
        name: keyToCheck,
        business_id: business_token,
        store_name: store_name,
        store_id: storeId,
        latitude: latitude,
        longitude: longitude,
      };

      await new Promise((resolve, reject) => {
        const request = objectStore.add(newData);
        request.onsuccess = resolve;
        request.onerror = (event) => reject(event.target.error);
      });

      console.log('Config data added to IndexedDB');
      result.localDBResponse = true;

      // Add the record to cloud DB
      try {
        const cloudData = {
          business_id: business_token,
          name: store_name,
          store_id: storeId,
          latitude: latitude,
          longitude: longitude,
        };

        const response = await fetch(`${api}/store`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(cloudData),
        });

        if (response.ok) {
          console.log('Store config data saved in cloud successfully');
          result.cloudDBResponse = true;
        } else {
          console.error('Error saving store config data in cloud:', response.statusText);
          result.cloudDBError = response.statusText;
        }
      } catch (error) {
        console.error('Error sending store config data to cloud:', error);
        result.cloudDBError = error;
      }
    }
  } catch (error) {
    console.error('Error inserting into table settings:', error);
    result.localDBError = error;
  }

  return result;
}


// function to check if config data is already present in indexedDB
// if present then return true else false
export async function checkConfigData() {
  try {
    const db = await initDb();
    if (!db) {
      throw new Error('Database not initialized');
    }

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['settings'], 'readonly');
      const objectStore = transaction.objectStore('settings');
      const request = objectStore.get('configData');

      request.onsuccess = (event) => {
        if (event.target.result) {
          resolve(true);
        } else {
          resolve(false);
        }
      };

      request.onerror = (event) => {
        console.error('Error fetching config data in table settings', event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error('Error fetching config data in table settings', error);
    return false;
  }
}



export async function insertQuestion1(device_id, response_value, datetime_, payload_adelaide_oval, customer_feedback_id) {
  //console.log(`insert q1: device_id: ${device_id}, response_value: ${response_value}, datetime_: ${datetime_}`);
  try {
    const db = await initDb(); // Ensure the database is initialized properly
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['question_1_scale'], 'readwrite');
      // complete transaction
      transaction.oncomplete = () => {
        //console.log('Transaction completed for question_1_scale');
      };
      transaction.onerror = (event) => {
        console.error('Transaction error for question_1_scale:', event.target.error);
        reject(event.target.error);
      };

      const objectStore = transaction.objectStore('question_1_scale');

      const request = objectStore.add({ 
        device_id: device_id,
        response_value: response_value,
        datetime_: datetime_,
        payload_adelaide_oval: payload_adelaide_oval,
        customer_feedback_id: customer_feedback_id,
        synced: 0 
      });
      
      request.onsuccess = () => {
        resolve();
        //console.log('Question 1 data added');
      };
  
      request.onerror = (event) => {
        console.error('Error adding question 1 data:', event.target.error);
        reject(event.target.error);
      };
    });
  }
  catch (error) {
    console.error('Error inserting into table question1 :', error);
  }
}
  

export function insertQuestion2(device_id, response_value, datetime_, customer_feedback_id) {
  //console.log(`insert q2: device_id: ${device_id}, response_value: ${response_value}, datetime_: ${datetime_}`);
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['question_2_yesno'], 'readwrite');
    const objectStore = transaction.objectStore('question_2_yesno');
    const request = objectStore.add({ device_id, response_value, datetime_, customer_feedback_id, synced: 0 });

    request.onsuccess = () => {
      resolve();
      //console.log('Question 2 data added');
    };

    request.onerror = (event) => {
      console.error('Error adding question 2 data:', event.target.error);
      reject(event.target.error);
    };
  });
}

export function insertQuestion3(device_id, response_value, datetime_, customer_feedback_id) {
  //console.log(`insert q3: device_id: ${device_id}, response_value: ${response_value}, datetime_: ${datetime_}`);
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['question_3_yesno'], 'readwrite');
    const objectStore = transaction.objectStore('question_3_yesno');
    const request = objectStore.add({ device_id, response_value, datetime_, customer_feedback_id, synced: 0 });

    request.onsuccess = () => {
      resolve();
      //console.log('Question 3 data added');
    };

    request.onerror = (event) => {
      console.error('Error adding question 3 data:', event.target.error);
      reject(event.target.error);
    };
  });
}

export function insertComplaint(device_id, datetime_, customer_feedback_id, complaint_id) {
  //console.log(`insert complaint: device_id: ${device_id}, datetime_: ${datetime_}, customer_feedback_id: ${customer_feedback_id}, complaint_id: ${complaint_id}`);
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['complaint'], 'readwrite');
    const objectStore = transaction.objectStore('complaint');
    const request = objectStore.add({ device_id, datetime_, customer_feedback_id, complaint_id, synced: 0 });

    request.onsuccess = () => {
      resolve();
      //console.log('Complaint data added');
    };

    request.onerror = (event) => {
      console.error('Error adding complaint data:', event.target.error);
      reject(event.target.error);
    };
  });
}

// fetch unsynced data from the question_1_scale
const fetchUnsyncedQuestion1 = async () => {
  try {
    const db = await initDb();
    if (!db) {
      throw new Error('Database not initialized');
    }

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['question_1_scale'], 'readonly');
      const objectStore = transaction.objectStore('question_1_scale');
      const index = objectStore.index('syncedIndex');

      const request = index.getAll(IDBKeyRange.only(0)); // Fetch all records where synced is 0

      request.onsuccess = (event) => {
        resolve(event.target.result);
      };

      request.onerror = (event) => {
        console.error('Error fetching unsynced data in table question_1_scale', event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error('Error fetching unsynced data in table question_1_scale', error);
    return [];
  }
};


// fetch unsynced data from the question_2_yesno
const fetchUnsyncedQuestion2 = async () => {
  try {
    const db = await initDb();
    if (!db) {
      throw new Error('Database not initialized');
    }

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['question_2_yesno'], 'readonly');
      const objectStore = transaction.objectStore('question_2_yesno');
      const index = objectStore.index('syncedIndex');

      const request = index.getAll(IDBKeyRange.only(0)); // Fetch all records where synced is 0

      request.onsuccess = (event) => {
        resolve(event.target.result);
      };

      request.onerror = (event) => {
        console.error('Error fetching unsynced data in table question_2_yesno', event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error('Error fetching unsynced data in table question_2_yesno', error);
    return [];
  }
};

// fetch unsynced data from the question_3_yesno
const fetchUnsyncedQuestion3 = async () => {
  try {
    const db = await initDb();
    if (!db) {
      throw new Error('Database not initialized');
    }
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['question_3_yesno'], 'readonly');
      const objectStore = transaction.objectStore('question_3_yesno');
      const index = objectStore.index('syncedIndex');

      const request = index.getAll(IDBKeyRange.only(0)); // Fetch all records where synced is 0

      request.onsuccess = (event) => {
        resolve(event.target.result);
      };

      request.onerror = (event) => {
        console.error('Error fetching unsynced data in table question_3_yesno', event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error('Error fetching unsynced data in table question_3_yesno', error);
    return [];
  }
};

// fetch unsynced data from the complaint
const fetchUnsyncedComplaint = async () => {
  try {
    const db = await initDb();
    if (!db) {
      throw new Error('Database not initialized');
    }

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['complaint'], 'readonly');
      const objectStore = transaction.objectStore('complaint');
      const index = objectStore.index('syncedIndex');

      const request = index.getAll(IDBKeyRange.only(0)); // Fetch all records where synced is 0

      request.onsuccess = (event) => {
        resolve(event.target.result);
      };

      request.onerror = (event) => {
        console.error('Error fetching unsynced data in table complaint', event.target.error);
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error('Error fetching unsynced data in table complaint', error);
    return [];
  }
};


// mark data as synced for question_1_scale
export function markQuestion1DataAsSynced() {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['question_1_scale'], 'readwrite');
    const objectStore = transaction.objectStore('question_1_scale');
    const request = objectStore.openCursor();

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        const updatedData = cursor.value;
        updatedData.synced = 1;
        cursor.update(updatedData);
        cursor.continue();
      } else {
        resolve();
      }
    };

    request.onerror = (event) => {
      console.error('Error marking question 1 data as synced:', event.target.error);
      reject(event.target.error);
    };
  });
}

// mark data as synced for question_2_yesno
export function markQuestion2DataAsSynced() {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['question_2_yesno'], 'readwrite');
    const objectStore = transaction.objectStore('question_2_yesno');
    const request = objectStore.openCursor();

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        const updatedData = cursor.value;
        updatedData.synced = 1;
        cursor.update(updatedData);
        cursor.continue();
      } else {
        resolve();
      }
    };

    request.onerror = (event) => {
      console.error('Error marking question 2 data as synced:', event.target.error);
      reject(event.target.error);
    };
  });
}

// mark data as synced for question_3_yesno
export function markQuestion3DataAsSynced() {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['question_3_yesno'], 'readwrite');
    const objectStore = transaction.objectStore('question_3_yesno');
    const request = objectStore.openCursor();

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        const updatedData = cursor.value;
        updatedData.synced = 1;
        cursor.update(updatedData);
        cursor.continue();
      } else {
        resolve();
      }
    };

    request.onerror = (event) => {
      console.error('Error marking question 3 data as synced:', event.target.error);
      reject(event.target.error);
    };
  });
}

// mark data as synced for complaint
export function markComplaintDataAsSynced() {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['complaint'], 'readwrite');
    const objectStore = transaction.objectStore('complaint');
    const request = objectStore.openCursor();

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        const updatedData = cursor.value;
        updatedData.synced = 1;
        cursor.update(updatedData);
        cursor.continue();
      } else {
        resolve();
      }
    };

    request.onerror = (event) => {
      console.error('Error marking complaint data as synced:', event.target.error);
      reject(event.target.error);
    };
  });
}

// sync data to the cloud
export async function syncDataToCloud() {
  console.log('Syncing data to cloud');
  try {
  const question1Data = await fetchUnsyncedQuestion1();
  const question2Data = await fetchUnsyncedQuestion2();
  const question3Data = await fetchUnsyncedQuestion3();
  const complaintData = await fetchUnsyncedComplaint();

  const data = {
    question1: question1Data,
    question2: question2Data,
    question3: question3Data,
    complaint: complaintData,
  };

    // if there is no data to sync, return early
    if (question1Data.length === 0 && question2Data.length === 0 && question3Data.length === 0 && complaintData.length === 0) {
      console.log('No data to sync');
      return;
    }
    
    // send external feedback for question 1 data for adelaide oval
    try {
      for (const entry of question1Data) {
        const externalPayload = entry.payload_adelaide_oval;
        console.log(`externalPayload for question 1 entry id ${entry.id}:`, externalPayload);
        // only send if payload is not empty
        if (Object.keys(externalPayload).length !== 0) {
          const org_id = 1; // adelaide oval org_id is 1
          const externalResponse = await sendExternalFeedback(externalPayload, org_id);
          if (externalResponse.success) {
            console.log('External feedback sent successfully for question 1 entry id:', entry.id);
          } else {
            console.error('Failed to send external feedback for question 1 entry id:', entry.id, 'Error:', externalResponse.error);
          }
        }
      }
    } catch (externalError) {
      console.error('Error sending external feedback to adelaide oval for question 1 data:', externalError);
    }

    const response = await fetch(`${api}/feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    
    if (response.ok) {
      // mark data as synced
      await markQuestion1DataAsSynced();
      await markQuestion2DataAsSynced();
      await markQuestion3DataAsSynced();
      await markComplaintDataAsSynced();
      console.log('Data synced to cloud');
    }
    else {
      console.error('Error syncing data:', response.statusText);
    }
  } catch (error) {
    console.error('Error syncing data:', error);
  }
}


// remove synced entries
export async function removeSyncedEntries(storeName) {
  const db = await initDb(); 

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readwrite');
    const objectStore = transaction.objectStore(storeName);
    const index = objectStore.index('syncedIndex');
    const request = index.openCursor(IDBKeyRange.only(1)); // Fetch all records where synced is 1

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) { // If there is an entry
        objectStore.delete(cursor.primaryKey); // Remove the entry
        //console.log(`synced entry removed from ${storeName}`);
        cursor.continue(); // Move to the next entry
      } else { // If there are no more entries
        resolve();
        //console.log(`All synced entries already removed from ${storeName}`);
      }
    };

    request.onerror = (event) => {
      console.error(`Error removing synced entries in ${storeName}:`, event.target.error);
      reject(event.target.error);
    };
  });
}

// Direct sync to RDS for virtual devices (bypasses IndexedDB)
export async function syncDirectToRDS(data) {
  try {
    console.log('Direct sync to RDS for virtual device:', data);
    
    const response = await fetch(`${api}/feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (response.ok) {
      console.log('Data synced directly to RDS');
      return { success: true };
    } else {
      console.error('Error syncing data to RDS:', response.statusText);
      return { success: false, error: response.statusText };
    }
  } catch (error) {
    console.error('Error syncing data to RDS:', error);
    return { success: false, error: error.message };
  }
}

// schedule removing synced entries every 10 minutes
export async function scheduleRemoveSyncedEntries() {
    try {
      await removeSyncedEntries('question_1_scale');
      await removeSyncedEntries('question_2_yesno');
      await removeSyncedEntries('question_3_yesno');
      await removeSyncedEntries('complaint');
    } catch (error) {
      console.error('Error removing synced entries:', error);
    }
}